import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Card, Stack, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import {
  ContractItemParam,
  QuoteItemParam,
  userCanSeePriceReference,
} from "~/features/cart/cart/components/LineItemStaticParams";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { Parameter } from "~/common/components/ParameterV2";
import { getEDDLabel } from "~/features/cart/cart/utils/cartUtils";
import { useViewableLineItemData } from "~/domains/cart/hooks";
import { LineItemMessagesSection } from "~/domains/cart/components";
import { ViewablePackagingCell } from "~/features/cart/cart/components/PackagingCell";
import { LineItemStockIndicator } from "~/features/cart/cart/components/LineItemStockIndicator";
import { NumericFormat } from "react-number-format";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { PartialDeliveriesSection } from "./PartialDeliveriesSection";
import { IncludedSurchargeAsterisk, TariffSurchargeAsterisk } from "./commonComponents";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { FormStaticText } from "~/common/components/FormStaticText/FormStaticText";

interface CheckoutLineItemProps {
  className?: string;
  lineItem: LineItem;
  product: ProductDescriptor;
}

/**
 * Grid layout for checkout line item details.
 * Uses CSS Grid with 12 columns for responsive layout.
 */
const DetailsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "16px",
  rowGap: "16px",

  [theme.breakpoints.up("desktop-m")]: {
    gridTemplateColumns: "180px repeat(5, minmax(0px, 1fr))",
  },

  ["> .stock-indicator"]: {
    gridColumn: "1 / -1",
    justifySelf: "flex-start",

    [theme.breakpoints.up("desktop-m")]: {
      alignSelf: "flex-start",
      gridColumn: "1 / span 1",
      gridRow: 1,
    },
  },

  ["> .quantity"]: {
    gridColumn: "span 4",
    width: "168px",

    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "2 / span 1",
      gridRow: 1,
    },
  },

  ["> .packaging"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "3 / span 1",
      gridRow: 1,
    },
  },

  ["> .delivery-date"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "4 / span 1",
      gridRow: 1,
    },
  },

  ["> .net-price"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "5 / span 1",
      gridRow: 1,
    },
  },

  ["> .total-price"]: {
    gridColumn: "span 4",
    justifySelf: "flex-start",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "6 / span 1",
      gridRow: 1,
    },
  },

  ["> .customer-order-number"]: {
    gridColumn: "1 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "2 / span 1",
      gridRow: 2,
    },
  },

  ["> .aliases"]: {
    gridColumn: "1 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "3 / span 1",
      gridRow: 2,
    },
  },

  ["> .contract-details, > .quote-details"]: {
    gridColumn: "9 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "4 / span 1",
      gridRow: 2,
    },
  },

  ["> .price-reference"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "5 / span 1",
      gridRow: 2,
    },
  },

  ["> .gpsd"]: {
    gridColumn: "5 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "6 / span 1",
      gridRow: 2,
    },
  },

  ["> .messages"]: {
    gridColumn: "1 / -1",
    gridRow: 3,
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "1 / span 4",
      gridRow: 3,
    },
  },
}));

/**
 * Read-only line item component for checkout page.
 * Shows the same data as the cart line item but without edit functionality.
 */
export const CheckoutRegularProductLine = ({ className, lineItem, product }: CheckoutLineItemProps) => {
  const { t } = useTranslation();

  // Use viewable (read-only) data hook
  const { pricing, flags, formatted } = useViewableLineItemData({ lineItem, product });

  const { quantityVisible, isPMA, isOptime, webservicesDisabled } = flags;
  const { totalPriceFormatted, hasSurcharge, surchargeValue, tariffSurcharge, zeroPriceLabel } = pricing;

  const showPriceReference = userCanSeePriceReference();
  const showEdd = true;

  // Price element
  const priceEl = webservicesDisabled ? null : (
    <PriceElements.Stack gap={0}>
      <PriceElements.Price
        variant="inherit"
        price={lineItem.netPrice}
        zeroText={zeroPriceLabel}
        tariffSurcharge={tariffSurcharge}
      />
    </PriceElements.Stack>
  );

  // Quantity formatted
  const quantityValue = (
    <NumericFormat
      value={formatted.quantity}
      displayType="text"
      thousandSeparator={NF.getThousandSeparator()}
      decimalSeparator={NF.getDecimalSeparator()}
    />
  );

  // Delivery date value
  const deliveryDateValue =
    quantityVisible && !isPMA ? (
      <FormStaticText
        className="delivery-date"
        label={t("cartPage.table.header.delivery.title")}
        value={formatted.deliveryDate}
        helperText={showEdd ? getEDDLabel(lineItem, t) : "-"}
      />
    ) : (
      <FormStaticText className="delivery-date" label={t("cartPage.table.header.delivery.title")} value={"-"} />
    );

  // Total price value
  const totalPriceValue = (
    <Stack>
      {!webservicesDisabled && (
        <TotalPrice>
          {totalPriceFormatted}
          {hasSurcharge && "*"}
        </TotalPrice>
      )}
    </Stack>
  );

  const aliases = ProductParams2.ProductAliases({
    mode: "15-digits",
    visibleAliasesLayout: "separate-rows",
    packCode: lineItem.packCode,
  })({
    t,
    product,
  });

  const gpsd = ProductParams2.GPSD({
    t,
    product,
  });

  return (
    <Card sx={{ p: 3 }} component={Stack} gap={3} className={className}>
      <DetailsGrid>
        {/* Stock Indicator */}
        <LineItemStockIndicator className="stock-indicator" product={product} packEntry={lineItem} />

        {/* Quantity - read only */}
        <Parameter className="quantity" label={t("input.quantity")} value={quantityValue} />

        {/* Net Price */}
        <Parameter
          className="net-price"
          label={<PriceElements.NetPriceLabel variant="inherit" price={lineItem.netPrice} />}
          value={priceEl}
        />

        {/* Total Price */}
        <Parameter
          className="total-price"
          label={<PriceElements.TotalPriceLabel variant="inherit" price={lineItem.netPrice} />}
          value={totalPriceValue}
        />

        {/* Delivery Date - read only */}
        {deliveryDateValue}

        {/* Packaging - read only */}
        <div className="packaging">
          <ViewablePackagingCell lineItem={lineItem} product={product} />
        </div>

        {/* Price Reference - read only */}
        {showPriceReference && (
          <Parameter
            className="price-reference"
            label={t("cartPage.table.header.priceReference.title")}
            value={lineItem.priceReference || "-"}
          />
        )}

        {aliases && <Parameter className="aliases" {...aliases} />}

        {gpsd && <Parameter className="gpsd" {...gpsd} />}

        {/* Quote/Contract details */}
        <QuoteItemParam className="quote-details" product={product.base} lineItem={lineItem} />
        <ContractItemParam className="contract-details" product={product.base} lineItem={lineItem} />

        {isLineLevelCustomerOrderNumberEnabled() && lineItem.customerOrderNumber && (
          <Parameter
            className="customer-order-number"
            label={t("search.type.customerOrderNumber")}
            value={lineItem.customerOrderNumber}
          />
        )}

        {/* Messages */}
        <LineItemMessagesSection
          messages={[]}
          isPMA={isPMA}
          isOptime={isOptime}
          quantityVisible={quantityVisible}
          className="messages"
        />
      </DetailsGrid>

      <OptionalBox>
        {surchargeValue && <IncludedSurchargeAsterisk value={surchargeValue} percent={lineItem.netPrice?.surcharge} />}
        {tariffSurcharge && <TariffSurchargeAsterisk />}
      </OptionalBox>

      <PartialDeliveriesSection partialDeliveries={lineItem.partialDeliveries} />

      {lineItem.comment && (
        <Parameter
          label={t("wishlistPage.comment.label")}
          value={lineItem.comment}
          slotProps={{ value: { variant: "BodyMedium" } }}
        />
      )}
    </Card>
  );
};

const OptionalBox = styled("div")({
  ["&:empty"]: {
    display: "none",
  },
});

const TotalPrice = styled("strong")({
  fontWeight: "bold",
});
